import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserConfirm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading, success, already_confirmed, error
    const [message, setMessage] = useState('');
    const [user, setUser] = useState(null);
    
    const token = searchParams.get('token');

    useEffect(() => {
        if (token) {
            confirmEmail();
        } else {
            setStatus('error');
            setMessage('Ungültiger Bestätigungslink');
        }
    }, [token]);

    const confirmEmail = async () => {
        try {
            const res = await fetch(`${API_URL}/api/booking/auth/confirm?token=${token}`);
            const data = await res.json();
            
            if (res.ok) {
                if (data.status === 'already_confirmed') {
                    setStatus('already_confirmed');
                    setMessage('Ihre E-Mail-Adresse wurde bereits bestätigt.');
                } else {
                    setStatus('success');
                    setMessage('Ihre E-Mail-Adresse wurde erfolgreich bestätigt!');
                    setUser(data.user);
                    
                    // Auto-login the user
                    if (data.access_token) {
                        localStorage.setItem('booking_token', data.access_token);
                        toast.success('Sie sind jetzt angemeldet!');
                    }
                }
            } else {
                setStatus('error');
                setMessage(data.detail || 'Bestätigung fehlgeschlagen');
            }
        } catch (err) {
            console.error('Confirmation error:', err);
            setStatus('error');
            setMessage('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            
            <main className="pt-32 pb-16 px-4">
                <div className="max-w-md mx-auto text-center">
                    {status === 'loading' && (
                        <div className="space-y-4">
                            <Loader2 className="w-16 h-16 mx-auto animate-spin text-black" />
                            <p className="text-concrete-600">E-Mail wird bestätigt...</p>
                        </div>
                    )}
                    
                    {status === 'success' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-bold">Willkommen beim teamWERK!</h1>
                            <p className="text-concrete-600">{message}</p>
                            
                            {user && (
                                <p className="text-sm text-concrete-500">
                                    Angemeldet als: <strong>{user.first_name} {user.last_name}</strong>
                                </p>
                            )}
                            
                            <div className="space-y-3 pt-4">
                                <Button 
                                    onClick={() => navigate('/mein-bereich')} 
                                    className="w-full bg-black hover:bg-black/90"
                                >
                                    Zu meinem Bereich
                                </Button>
                                <Button 
                                    variant="outline" 
                                    onClick={() => navigate('/buchung')}
                                    className="w-full"
                                >
                                    Raum buchen
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {status === 'already_confirmed' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle className="w-10 h-10 text-blue-600" />
                            </div>
                            <h1 className="text-2xl font-bold">Bereits bestätigt</h1>
                            <p className="text-concrete-600">{message}</p>
                            
                            <Button 
                                onClick={() => navigate('/login')} 
                                className="bg-black hover:bg-black/90"
                            >
                                Zum Login
                            </Button>
                        </div>
                    )}
                    
                    {status === 'error' && (
                        <div className="space-y-6">
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                                <XCircle className="w-10 h-10 text-red-600" />
                            </div>
                            <h1 className="text-2xl font-bold">Bestätigung fehlgeschlagen</h1>
                            <p className="text-concrete-600">{message}</p>
                            
                            <div className="space-y-3 pt-4">
                                <Button 
                                    onClick={() => navigate('/login')} 
                                    className="bg-black hover:bg-black/90"
                                >
                                    Zum Login
                                </Button>
                                <p className="text-sm text-concrete-500">
                                    Bei der Anmeldung wird automatisch eine neue Bestätigungs-E-Mail gesendet.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default UserConfirm;
